/**
 * Size Tier
 *
 * The class of a print area that tells the carry-over rule how to adapt a
 * design onto it. Derived from the physical width in mm, never from pixels
 * or aspect ratio: a polo chest (75x100 mm) and a shirt front (330x450 mm)
 * share almost the same ratio but are worlds apart in size.
 *
 * - KLEIN: small areas (mugs, polo chests, caps, phone cases) -- the whole
 *   motif is maximised inside the area when it comes from a larger source.
 * - STANDARD: shirts, hoodies, bags, posters -- the relative rule applies.
 * - RUNDUM: wrap-around areas (panorama mugs); admin-set only. A design from
 *   any other tier is maximised inside the front half of the strip.
 */

import type {
  CarryOverAnchor,
  ProductClass,
} from '../design/carryOverDefaults.js';

export type SizeTier = 'KLEIN' | 'STANDARD' | 'RUNDUM';

export const SIZE_TIERS: readonly SizeTier[] = ['KLEIN', 'STANDARD', 'RUNDUM'];

/**
 * Areas narrower than this (max width in mm) are KLEIN. The catalogue has
 * no area between 155 and 210 mm, so the threshold sits in that gap.
 */
export const SIZE_TIER_THRESHOLD_MM = 180;

/**
 * The one shape every placement function takes for a print area: its size
 * (the only thing the math needs), plus the physical width and the tier when
 * the caller has them. The rules are relative and unit-free; a stored
 * placement is computed on the area's millimetres (`placementAreaFromMm`,
 * R44), so `width`/`height` are millimetres there. The web editor still
 * passes canvas pixels of its in-memory print context until U13.
 */
export interface PlacementArea {
  /** Width of the area (millimetres for stored placements) */
  width: number;
  /** Height of the area (millimetres for stored placements) */
  height: number;
  /** Physical width in mm (optional; enables the master-width cap) */
  maxWidthMm?: number | null;
  /** Physical height in mm (optional; carried for callers that have it) */
  maxHeightMm?: number | null;
  /** Effective size tier (optional; enables the KLEIN branch) */
  sizeTier?: SizeTier | null;
  /**
   * Admin override of the tier, when the caller has the catalogue row.
   * Since the carry-over settings, only RUNDUM is set by hand; a KLEIN
   * override still in the database (before the retire migration ran) is
   * read as carry-over percent 100, centered.
   */
  sizeTierOverride?: SizeTier | null;
  /**
   * The product this area belongs to. On a TARGET it identifies the product
   * being rendered; on a SOURCE (read from a row's snapshot) the product the
   * stored state was measured on. Equal ids mean rule 1: the row describes
   * this very area and is used as-is.
   */
  productId?: string | null;
  /** Product class of the area's product, when known (rules 3a/3b). */
  productClass?: ProductClass | null;
  /**
   * Effective carry-over percent of a TARGET area (the area's own value,
   * else its product's). Null or absent means "no percent rule".
   */
  carryOverPercent?: number | null;
  /** Effective vertical anchor of a TARGET area; null means class default. */
  carryOverAnchor?: CarryOverAnchor | null;
  /** Top offset in mm for anchor TOP; null means the global default. */
  carryOverTopOffsetMm?: number | null;
}

/** Fields of a print area that decide its effective tier. */
export interface SizeTierFields {
  sizeTier?: SizeTier | null;
  sizeTierOverride?: SizeTier | null;
  /** Physical width, used to derive the tier when neither tier field is set */
  maxWidthMm?: number | null;
}

export function isSizeTier(value: unknown): value is SizeTier {
  return (
    typeof value === 'string' &&
    (SIZE_TIERS as readonly string[]).includes(value)
  );
}

/**
 * Derive the tier from the physical width. Missing, zero, negative or
 * non-finite widths are STANDARD: an area we know nothing about keeps the
 * relative rule, which is today's behaviour.
 */
export function deriveSizeTier(
  maxWidthMm: number | null | undefined
): SizeTier {
  if (
    typeof maxWidthMm !== 'number' ||
    !Number.isFinite(maxWidthMm) ||
    maxWidthMm <= 0
  ) {
    return 'STANDARD';
  }
  return maxWidthMm < SIZE_TIER_THRESHOLD_MM ? 'KLEIN' : 'STANDARD';
}

/**
 * The tier that applies to an area: the admin override when set, otherwise
 * the derived tier, otherwise derived from the physical width (rows or
 * payloads that predate the tier column), otherwise STANDARD.
 */
export function effectiveSizeTier(area: SizeTierFields): SizeTier {
  if (isSizeTier(area.sizeTierOverride)) return area.sizeTierOverride;
  if (isSizeTier(area.sizeTier)) return area.sizeTier;
  return deriveSizeTier(area.maxWidthMm);
}

/**
 * Whether the carry-over takes the placement from the target area instead of
 * the master: a KLEIN or RUNDUM target reached from a source whose tier is
 * known and different. The one place that condition is written -- the
 * carry-over branches on it, the editor marks a maximised switch with it and
 * the product match skips its width check with it.
 */
export function maximisesOnTier(
  sourceTier: SizeTier | null | undefined,
  targetTier: SizeTier | null | undefined
): boolean {
  return (
    (targetTier === 'KLEIN' || targetTier === 'RUNDUM') &&
    sourceTier !== undefined &&
    sourceTier !== null &&
    sourceTier !== targetTier
  );
}

/**
 * The subset of an in-memory print context the source tier is read from.
 *
 * @deprecated Web editor only (`areaFromPrintContext`, the catalogue
 * filter); stored rows carry no print context since Release 2. U13 removes
 * it together with `contextWidthMm` and `sourceTierFromContext`.
 */
export interface SourceTierContext {
  sizeTier?: unknown;
  widthMm?: unknown;
  printWidthPx?: unknown;
  pxPerMm?: unknown;
  mmFromPixels?: unknown;
}

/**
 * The physical width recorded in a print context, or `null` when the
 * context has none or its "mm" are really pixels (flagged as
 * `mmFromPixels`, or recognisable by `pxPerMm` 1 and an mm width equal to
 * the pixel width).
 *
 * @deprecated Web editor only, see `SourceTierContext` (U13).
 */
export function contextWidthMm(
  ctx: SourceTierContext | null | undefined
): number | null {
  if (!ctx) return null;
  const widthMm = ctx.widthMm;
  if (
    typeof widthMm !== 'number' ||
    !Number.isFinite(widthMm) ||
    widthMm <= 0
  ) {
    return null;
  }
  if (ctx.mmFromPixels === true) return null;
  const printWidthPx = ctx.printWidthPx;
  const pxPerMm = ctx.pxPerMm;
  const looksLikePxFallback =
    typeof printWidthPx === 'number' &&
    printWidthPx === widthMm &&
    (pxPerMm === undefined || pxPerMm === 1);
  return looksLikePxFallback ? null : widthMm;
}

/**
 * The tier of the SOURCE area of a stored design state: derived from the
 * snapshot's mm width first, from the recorded `sizeTier` only when the
 * snapshot has no usable width. Returns `undefined` when the tier is
 * unknown: no context, no usable mm width and no recorded tier. An unknown
 * source tier means "relative rule", never "STANDARD" -- a mug master must
 * keep behaving as KLEIN toward a shirt.
 *
 * Millimetres win over the recorded tier because writers record the
 * EFFECTIVE tier including an admin override: a poster master with a KLEIN
 * override counted as a KLEIN source, so poster -> mug stayed relative and
 * every other product was flagged "too narrow". The physical width is what
 * the source really is.
 *
 * @deprecated Web editor only, see `SourceTierContext` (U13).
 */
export function sourceTierFromContext(
  ctx: SourceTierContext | null | undefined
): SizeTier | undefined {
  if (!ctx) return undefined;
  // RUNDUM is admin-set and never follows from a width; the snapshot is the
  // only place it can be read from.
  if (ctx.sizeTier === 'RUNDUM') return 'RUNDUM';
  const widthMm = contextWidthMm(ctx);
  if (widthMm !== null) return deriveSizeTier(widthMm);
  return isSizeTier(ctx.sizeTier) ? ctx.sizeTier : undefined;
}
